import { useState, type ReactElement, type ReactNode } from 'react';
import { Confirm, useAdmin } from '../ui/Shell';
import { Banner, Field, Kv, List, type Act, type Cell as CellT, type Col, type Row } from '../ui/Block';
import { call, del, fmtBytes, fmtDate, patch, post, type Env } from '../lib/api';
import { describeErr, hhmmss, useData, type Fetched } from '../lib/hooks';
import { confirmSimple, DBlock } from './dataShared';

export interface ApiKey { id: string; principalId: string; name: string; prefix: string; scopes: string[]; rateLimitPerMin: number | null; createdAt: string; lastUsedAt: string | null; revokedAt: string | null; suspendedAt: string | null; suspendedReason: string | null; ownerSuspendedAt: string | null; ownerEmail: string | null; ownerName: string | null }
export interface Principal { suspendedAt: string | null; suspendedReason: string | null; deletedAt?: string | null; purgeAfter?: string | null }
export interface Counts { broadcasts: number; transcriptions: number; recordings: number; voices: number; keys: number }
interface UserDetail extends Principal {
  id: string; principalId: string; workosUserId: string; email: string; name: string; orgs: string | null; createdAt: string; lastLoginAt: string | null;
  counts: Counts; bytes: number; minutes30d: number; idpDeletion: string | null;
  voices: { id: string; name: string; state: string; sampleS: number | null; uses: number; createdAt: string }[];
  devices: { id: string; platform: string | null; firstSeenAt: string | null; lastSeenAt: string | null }[];
  soleOwnerOf: { id: string; name: string }[];
}

export const pl = (n: number, w: string) => `${n.toLocaleString()} ${w}${n === 1 ? '' : 's'}`;
export const errOf = (f: Fetched<unknown>) => (describeErr(f.error) ? { ...describeErr(f.error)!, retry: f.reload } : null);
export function copy(text: string) { try { void navigator.clipboard.writeText(text); } catch { } }

function RateLimitModal({ env, k, onDone }: { env: Env; k: ApiKey; onDone: () => void }) {
  const ctx = useAdmin();
  const [v, setV] = useState(k.rateLimitPerMin != null ? String(k.rateLimitPerMin) : '');
  const n = Math.round(Number(v));
  return (
    <Confirm env={env} app={`${k.name} · ${k.ownerEmail ?? k.principalId}`} mobile={ctx.mobile} title={`Rate limit for ${k.prefix}`} confirmLabel={Number.isFinite(n) && n > 0 ? 'Save' : undefined} onClose={ctx.closeModal} deskOnly
      onConfirm={async () => { await patch(env, `/admin/api-keys/${k.id}`, { rate_limit_per_min: n }); ctx.toast(`${k.prefix} limited to ${n}/min`); onDone(); }}>
      <Field label="Requests per minute" type="number" value={v} onChange={setV} autoFocus mono />
    </Confirm>
  );
}

type Ctx = ReturnType<typeof useAdmin>;

export function suspendActs(ctx: Ctx, kind: 'principal' | 'key', id: string, label: string, s: Principal, reload: () => void, effect: string): Act[] {
  const path = kind === 'principal' ? `/admin/principals/${id}` : `/admin/api-keys/${id}`;
  const after = () => { reload(); if (kind === 'principal') ctx.reloadAttention(); };
  if (s.deletedAt) {
    return [{ label: 'Restore account', onClick: () => ctx.openModal(
      <Confirm env={ctx.env} app={`${label} · ${id}`} mobile={ctx.mobile} title={`Restore ${label}`} confirmLabel="Restore account" onClose={ctx.closeModal} deskOnly
        body={`Deleted by the customer ${fmtDate(s.deletedAt)}; restorable until ${s.purgeAfter ? fmtDate(s.purgeAfter) : 'the end of its window'}, then due for purge. Restoring brings back the account, its API keys, shared links and listener pages, and calls off the purge. They are emailed that it is active again.`}
        onConfirm={async () => { await post(ctx.env, `${path}/unsuspend`); ctx.toast(`Restored ${label}`); after(); }} />,
    ) }];
  }
  if (s.suspendedAt) {
    return [{ label: 'Unsuspend', onClick: () => ctx.openModal(
      <Confirm env={ctx.env} app={`${label} · ${id}`} mobile={ctx.mobile} title={`Lift the suspension of ${label}`} confirmLabel="Unsuspend" onClose={ctx.closeModal} deskOnly
        body={`Suspended ${fmtDate(s.suspendedAt)}: “${s.suspendedReason ?? ''}”. Requests work again from the next call.`}
        onConfirm={async () => { await post(ctx.env, `${path}/unsuspend`); ctx.toast(`Unsuspended ${label}`); after(); }} />,
    ) }];
  }
  return [{ label: 'Suspend', danger: true, onClick: () => ctx.openModal(
    <Confirm env={ctx.env} app={`${label} · ${id}`} mobile={ctx.mobile} danger title={`Suspend ${label}`} confirmLabel="Suspend" onClose={ctx.closeModal} deskOnly
      body={`${effect} They are told “This account is suspended. Contact support at support@vocalstack.com.”; the reason stays here. Unsuspend lifts it.`}
      reason={{ label: 'Reason (admin only)', placeholder: 'abuse, chargeback, …' }}
      onConfirm={async (reason) => { await post(ctx.env, `${path}/suspend`, { reason }); ctx.toast(`Suspended ${label}`, reason); after(); }} />,
  ) }];
}

export function ActButtons({ acts, mobile }: { acts: Act[]; mobile: boolean }) {
  return <>{acts.map((a) => <button key={a.label} type="button" className={`btn btn--sm ${a.danger ? 'btn--danger' : ''}`} disabled={mobile} title={mobile ? 'Desktop only' : a.title} onClick={a.onClick}>{a.label}</button>)}</>;
}

export const purgeDue = (s: Principal) => !!s.deletedAt && !!s.purgeAfter && new Date(s.purgeAfter).getTime() <= Date.now();

export const stateCell = (s: Principal): CellT =>
  s.deletedAt ? { pill: { label: purgeDue(s) ? 'PURGE DUE' : 'DELETED', tone: 'bad' }, sub: s.purgeAfter ? `${purgeDue(s) ? 'due since' : 'due'} ${fmtDate(s.purgeAfter)}` : undefined }
  : s.suspendedAt ? { pill: { label: 'SUSPENDED', tone: 'bad' }, sub: s.suspendedReason ?? undefined } : { pill: { label: 'ACTIVE', tone: 'ok' } };

export function keyActs(ctx: Ctx, k: ApiKey, reload: () => void): Act[] {
  if (k.revokedAt) return [];
  const owner = k.ownerEmail ?? k.ownerName ?? k.principalId;
  return [
    { label: 'Rate limit', onClick: () => ctx.openModal(<RateLimitModal env={ctx.env} k={k} onDone={reload} />) },
    ...suspendActs(ctx, 'key', k.id, k.prefix, k, reload, `Requests with “${k.name}” (${k.prefix}), owned by ${owner}, fail with 403 from the next call.`),
    { label: 'Revoke', danger: true, onClick: () => ctx.openModal(
      <Confirm env={ctx.env} app={`${k.name} · owner ${owner}`} mobile={ctx.mobile} danger title={`Revoke API key ${k.prefix}`} confirmLabel={`Revoke ${k.prefix}`} onClose={ctx.closeModal} deskOnly
        body={`Revokes “${k.name}” (${k.prefix}), owned by ${owner}. Requests with it fail with 401 from the next call. Scopes: ${k.scopes.join(', ') || 'none'}. There is no undo.`}
        onConfirm={async () => { await del(ctx.env, `/admin/api-keys/${k.id}`); ctx.toast(`Revoked ${k.prefix}`, `${k.name} · ${owner}`); reload(); }} />,
    ) },
  ];
}

export const keyCols = (withOwner: boolean): Col[] => [
  { label: 'NAME', width: 'minmax(130px,1fr)', sort: 'name', sortAsc: true }, { label: 'PREFIX', width: '120px', sort: 'prefix', sortAsc: true },
  ...(withOwner ? [{ label: 'OWNER', width: 'minmax(150px,1.1fr)', sort: 'owner', sortAsc: true }] : []),
  { label: 'SCOPES', width: 'minmax(150px,1.3fr)', sort: 'scopes', sortAsc: true }, { label: 'RATE LIMIT', width: '90px', align: 'r' as const, sort: 'rate' },
  { label: 'CREATED', width: '110px', sort: 'created' }, { label: 'LAST USED', width: '110px', sort: 'used' }, { label: 'STATE', width: '96px', sort: 'state', sortAsc: true }, { label: '', width: 'minmax(150px,auto)', align: 'r' as const },
];

export function keyRows(ctx: Ctx, keys: ApiKey[], reload: () => void, withOwner: boolean, onRow?: (k: ApiKey) => void): Row[] {
  return keys.map((k) => ({
    key: k.id,
    onClick: onRow ? () => onRow(k) : undefined,
    cells: [
      { t: k.name, sub: k.revokedAt ? `revoked ${fmtDate(k.revokedAt)}` : undefined, fw: 500, fg: k.revokedAt ? 'var(--ink-45)' : undefined },
      { t: k.prefix, mono: true },
      ...(withOwner ? [{ t: k.ownerEmail ?? k.ownerName ?? '—', sub: k.ownerEmail && k.ownerName ? k.ownerName : undefined }] : []),
      { t: k.scopes.join(', ') || '—', mono: true },
      { t: k.rateLimitPerMin != null ? `${k.rateLimitPerMin}/min` : '—', mono: true },
      { t: fmtDate(k.createdAt) },
      { t: k.lastUsedAt ? fmtDate(k.lastUsedAt) : 'never', fg: k.lastUsedAt ? undefined : 'var(--ink-45)' },
      k.revokedAt ? { pill: { label: 'REVOKED', tone: 'mute' } }
        : k.suspendedAt ? { pill: { label: 'SUSPENDED', tone: 'bad' }, sub: k.suspendedReason ?? undefined }
        : k.ownerSuspendedAt ? { pill: { label: 'OWNER SUSPENDED', tone: 'warn' } }
        : { pill: { label: 'ACTIVE', tone: 'ok' } },
      { acts: keyActs(ctx, k, reload) },
    ],
  }));
}

export function UserProfile({ id, frame, onChange }: { id: string; frame: (acts: ReactNode, body: ReactNode) => ReactElement; onChange: () => void }) {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const d = useData<UserDetail>(env, `/admin/users/${id}`, 30, paused);
  const u = d.data;
  const reload = () => { d.reload(); onChange(); };

  const purgeAccount = (u: UserDetail) => {
    const list: [string, string][] = [
      ['Broadcasts', String(u.counts.broadcasts)],
      ['Transcriptions and recordings', `${u.counts.transcriptions} · ${pl(u.counts.recordings, 'recording')} · ${fmtBytes(u.bytes)}`],
      ['Voice samples', String(u.counts.voices)],
      ['API keys', String(u.counts.keys)],
      ...u.soleOwnerOf.map((o): [string, string] => [`Org ${o.name} (sole owner)`, 'everything it owns']),
    ];
    ctx.openModal(
      <Confirm env={env} app={`${u.name} · ${u.workosUserId}`} mobile={mobile} danger typed={u.email} deskOnly title="Purge account" confirmLabel="Purge everything" list={list} onClose={ctx.closeModal}
        body="Purging erases everything the account owns now, for good, including what belongs to organisations this user solely owns; it cannot be undone. Its billing records are kept for 7 years (purged_accounts). The WorkOS user is deleted by the IdP deletion job."
        onConfirm={async () => {
          const r = await call<{ email: string; broadcasts: number; transcriptions: number; voices: number; orgs: number; orgBroadcasts: number; orgTranscriptions: number }>(env, `/admin/users/${u.id}`, { method: 'DELETE', body: { confirm: u.email } });
          ctx.toast(`Purging ${r.email}`, `${r.broadcasts + r.transcriptions} broadcasts and transcriptions · ${r.voices} voices · ${r.orgs} organisations · IdP deletion queued`);
          ctx.reloadAttention();
          onChange();
          ctx.closeDrawer();
        }} />,
    );
  };
  const retryIdp = async (u: UserDetail) => {
    try { await post(env, `/admin/users/${encodeURIComponent(u.workosUserId)}/retry-idp`); ctx.toast(`Retrying WorkOS deletion for ${u.email}`); d.reload(); } catch (e) { ctx.toast((e as Error).message, '', { logged: false }); }
  };

  const impersonate = async (u: UserDetail) => {
    const w = window.open('', '_blank');
    try {
      const r = await post<{ url: string; email: string }>(env, `/admin/users/${u.id}/impersonate`);
      if (w) { w.opener = null; w.location.href = r.url; } else window.open(r.url, '_blank', 'noopener');
      ctx.toast(`Impersonating ${r.email}`, 'Read-only, for 30 minutes, in a new tab');
    } catch (e) {
      w?.close();
      ctx.toast((e as Error).message, '', { logged: false });
    }
  };

  const acts: ReactNode = u && (
    <>
      <button type="button" className="btn btn--sm btn--primary" title="Open the web app as this user, read-only, for 30 minutes" onClick={() => void impersonate(u)}>Impersonate</button>
      <button type="button" className="btn btn--sm" onClick={() => { copy(u.workosUserId); ctx.toast(`Copied ${u.workosUserId}`, 'Paste it into the WorkOS dashboard search', { logged: false }); }}>Copy WorkOS id</button>
      {u.idpDeletion && <button type="button" className="btn btn--sm" onClick={() => confirmSimple(ctx, { title: 'Retry the WorkOS deletion', app: u.email, danger: true, body: `Deletes ${u.email} from WorkOS again.`, confirmLabel: 'Retry deletion', onConfirm: () => retryIdp(u) })}>Retry WorkOS deletion</button>}
      <ActButtons mobile={mobile} acts={suspendActs(ctx, 'principal', u.principalId, u.email || u.name, u, reload, `${u.email || u.name} and every API key of their own get 403 from the next request; organisations they belong to are not affected.`)} />
      <button type="button" className="btn btn--sm btn--danger" disabled={mobile} title={mobile ? 'Desktop only' : undefined} onClick={() => purgeAccount(u)}>Purge account</button>
    </>
  );

  return frame(acts, (
    <>
      {!u && <DBlock title="Profile" loading={d.loading} error={errOf(d)} />}
      {u && u.deletedAt && (
        <DBlock bg="var(--conf-low-soft)"><Banner icon="warn" fg="var(--conf-low)" text="Deleted by the customer" sub={`Since ${fmtDate(u.deletedAt)} · ${purgeDue(u) ? `due for purge since ${fmtDate(u.purgeAfter)} (Purge account)` : `restorable until ${u.purgeAfter ? fmtDate(u.purgeAfter) : 'the end of its window'} (Restore account), then due for purge`} · requests answer 403 suspended`} /></DBlock>
      )}
      {u && u.suspendedAt && !u.deletedAt && (
        <DBlock bg="var(--conf-low-soft)"><Banner icon="warn" fg="var(--conf-low)" text="Suspended" sub={`Since ${fmtDate(u.suspendedAt)} · ${u.suspendedReason ?? ''} · requests answer 403 suspended`} /></DBlock>
      )}
      {u && u.idpDeletion && (
        <DBlock bg="var(--conf-mid-soft)"><Banner icon="warn" fg="var(--conf-mid)" text="Pending IdP deletion" sub={`The WorkOS user is still being deleted · job ${u.idpDeletion}`} /></DBlock>
      )}
      {u && (
        <>
          <DBlock title="Profile" asOf={hhmmss(d.at)}>
            <Kv rows={[
              { k: 'Display name', v: { t: u.name || '—' } },
              { k: 'WorkOS id', v: { t: u.workosUserId, mono: true } },
              { k: 'User id', v: { t: u.id, mono: true } },
              { k: 'Principal id', v: { t: u.principalId, mono: true } },
              { k: 'Organisations', v: { t: u.orgs || '—' } },
              { k: 'State', v: stateCell(u) },
              { k: 'Created', v: { t: fmtDate(u.createdAt) } },
              { k: 'Last login', v: { t: fmtDate(u.lastLoginAt) } },
              { k: 'Owns', v: countsCell(u.counts) },
              { k: 'Storage', v: { t: fmtBytes(u.bytes), mono: true } },
              { k: 'Minutes, 30 days', v: { t: Math.round(u.minutes30d).toLocaleString(), mono: true } },
            ]} />
          </DBlock>
          <DBlock title="Voices and devices" empty={u.voices.length + u.devices.length ? undefined : 'No voices or devices.'}>
            <List items={[
              ...u.voices.map((v) => ({ key: `v:${v.id}`, t: v.name, sub: `voice · ${v.state} · ${v.uses} uses`, right: fmtDate(v.createdAt) })),
              ...u.devices.map((v) => ({ key: `d:${v.id}`, t: <span className="mono">{v.id}</span>, sub: `device · ${v.platform ?? 'unknown platform'}`, right: v.lastSeenAt ? fmtDate(v.lastSeenAt) : undefined })),
            ]} />
          </DBlock>
        </>
      )}
    </>
  ));
}

export const countsCell = (c: Counts): CellT => ({ t: `${pl(c.broadcasts, 'broadcast')} · ${pl(c.transcriptions, 'transcription')}`, sub: `${pl(c.recordings, 'recording')} · ${pl(c.voices, 'voice')} · ${pl(c.keys, 'key')}` });
